import * as React from 'react';
import {
  SnackbarProvider,
  useSnackbar,
  SnackbarContent,
  OptionsObject,
  SnackbarMessage,
  VariantType,
} from 'notistack';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { styled } from '@mui/material';
import { red, green, orange } from '@mui/material/colors';
import { NotificationContent } from './NotificationContent';
import { SnackbarUtilsConfigurator } from './SnackBarUtils';

interface NotificationProps {
  id: string | number;
  title?: string;
  message: SnackbarMessage;
  variant: Partial<VariantType>;
}

function NotificationProvider({ children }: { children: React.ReactNode }) {
  return (
    <SnackbarProvider
      data-testid="notification"
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <SnackbarUtilsConfigurator />
      {children}
    </SnackbarProvider>
  );
}

function useNotification() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const handleNotify = (
    title: string,
    message: SnackbarMessage,
    options: OptionsObject | undefined
  ) => {
    return enqueueSnackbar?.(message, {
      ...options,
      content: (key) => {
        return (
          <Snackbar
            id={key}
            title={title}
            message={message}
            variant="success"
          />
        );
      },
    });
  };

  const handleWarningNotify = (
    title: string,
    message: SnackbarMessage,
    options: OptionsObject | undefined
  ) => {
    return enqueueSnackbar?.(message, {
      ...options,
      content: (key) => {
        return (
          <Snackbar
            id={key}
            title={title}
            message={message}
            variant="warning"
          />
        );
      },
    });
  };

  const handleErrorNotify = (
    title: string,
    message: SnackbarMessage,
    options: OptionsObject | undefined
  ) => {
    return enqueueSnackbar?.(message, {
      ...options,
      content: (key) => {
        return (
          <Snackbar id={key} title={title} message={message} variant="error" />
        );
      },
    });
  };

  return {
    notifySuccess: (
      title: string,
      message: SnackbarMessage,
      options?: OptionsObject | undefined
    ) => handleNotify(title, message, options),
    notifyError: (
      title: string,
      message: SnackbarMessage,
      options?: OptionsObject | undefined
    ) => handleErrorNotify(title, message, options),
    notifyWarning: (
      title: string,
      message: SnackbarMessage,
      options?: OptionsObject | undefined
    ) => handleWarningNotify(title, message, options),
    close: closeSnackbar,
  };
}

type MutationType = 'create' | 'save' | 'deactivate' | 'update' | 'delete';
/**
 * Creates standard mutation notifications
 * @param entityName singular name of the entity being mutated
 */
function useNotificationMutationFactory(
  entityName: string,
  mutationType: MutationType
) {
  const { notifyError: baseNotifyError, notifySuccess: baseNotifySuccess } =
    useNotification();

  const verbSet: Record<MutationType, string> = {
    create: 'Created',
    deactivate: 'Deactivated',
    save: 'Saved',
    update: 'Updated',
    delete: 'Deleted',
  };

  const verb = verbSet[mutationType];
  const lowercaseVerb = verb.toLowerCase();

  const notifySuccess = () =>
    baseNotifySuccess(
      `${entityName} ${verb}`,
      `${entityName} has been successfully ${lowercaseVerb}`
    );
  const notifyError = () =>
    baseNotifyError('Error', `${entityName} was not ${lowercaseVerb}`);
  return {
    notifySuccess,
    notifyError,
  };
}

const getBackgroundColor = (variant: string) => {
  switch (variant) {
    case 'success':
      return `${green[50]} !important`;
    case 'warning':
      return `${orange[100]} !important`;
    case 'error':
    default:
      return `${red[100]} !important`;
  }
};

const getColor = (variant: string) => {
  switch (variant) {
    case 'success':
      return `${green[900]} !important`;
    case 'warning':
      return `${orange[900]} !important`;
    case 'error':
    default:
      return `${red[900]}`;
  }
};

export const Snackbar = React.forwardRef<HTMLDivElement, NotificationProps>(
  ({ id, message, variant, title }, ref) => {
    const SnackBarStyles = styled('button')((props: { variant: string }) => ({
      backgroundColor: getBackgroundColor(props.variant),
      color: getColor(props.variant),
      borderRadius: '0.25rem',
      padding: '0.75rem',
      border: 'none',
      textAlign: 'left',
      display: 'flex',
      alignItems: 'flex-start',
      minWidth: '25vw',
      maxWidth: '25vw',
    }));

    const IconStyle = {
      fontSize: '1.5625rem',
      marginRight: '0.625rem',
      color: variant === 'success' ? `${green[500]}` : `${red[500]}`,
    };

    const renderIcon = (iconVariant: string) => {
      return iconVariant === 'success' ? (
        <CheckCircleOutlineOutlinedIcon sx={IconStyle} />
      ) : (
        <ErrorOutlineOutlinedIcon sx={IconStyle} />
      );
    };

    return (
      <SnackbarContent key={id} ref={ref}>
        <SnackBarStyles variant={variant}>
          {renderIcon(variant)}
          <NotificationContent title={title || ''}>
            {message as React.ReactChild | React.ReactChildren}
          </NotificationContent>
        </SnackBarStyles>
      </SnackbarContent>
    );
  }
);

export {
  NotificationProvider,
  useNotification,
  useNotificationMutationFactory,
};
